// ADR 8.10.26f §2–§3 — `lingara.param`: a setting a script declares, and the
// one rule for its value that the helper (in the sandbox worker) and the
// inspector (on the main thread) share, so the two cannot disagree.
//
// Three of these numbers are deliberate copies of a Backend rule (Backend ADR
// 8.10.26e §1, §3, §7): the handle grammar, the number window, 16 values a
// node and 512 bytes a value. They are mirrored so the inspector can say why
// before a save fails; the definition-wide cap stays the server's alone.
//
// Imported by the sandbox worker: nothing here touches the network, storage
// or time, and it value-imports only `dsl/` (2.10.26f AC13).

import type { ParamType, ParamValue } from './lingara.v1'
import { MAX_ITEMS, MAX_STRING } from './specs'

export type { ParamType, ParamValue }

export type ParamValues = Record<string, ParamValue>

export interface ParamDeclaration {
  name: string
  type: ParamType
  default: ParamValue
  label?: string
  choices?: readonly string[]
}

export type ParamProblem = 'too_long' | 'url' | 'number' | 'number_range'

export type ParamEntry = { ok: true; value: ParamValue | undefined } | { ok: false; problem: ParamProblem }

/** Backend 8.10.26e §1's `valid_handle`: handles, script names and param names alike. */
export const PARAM_NAME = /^[a-z][a-z0-9_-]{0,31}$/

/** Backend 8.10.26e §3's `MAX_PARAMS_PER_NODE`. */
export const MAX_PARAMS = 16

/** Backend 8.10.26e §3's `MAX_PARAM_VALUE_BYTES`: UTF-8 bytes of a string value. */
export const MAX_PARAM_VALUE_BYTES = 512

const PARAM_TYPES: readonly ParamType[] = ['string', 'url', 'number', 'boolean', 'choice']

const NUMBER_TEXT = /^-?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$/

/**
 * Backend 8.10.26e §1's number window: `0`, or `1e-5 ≤ |n| ≤ 2^53 − 1`. A
 * magnitude test, never a test of the text, because JS and the server's
 * formatter switch to exponent form at different magnitudes.
 */
export function storableNumber(n: number): boolean {
  if (!Number.isFinite(n)) return false
  const size = Math.abs(n)
  return size === 0 || (size >= 1e-5 && size <= Number.MAX_SAFE_INTEGER)
}

function fitsBytes(text: string): boolean {
  return new TextEncoder().encode(text).length <= MAX_PARAM_VALUE_BYTES
}

function isWebAddress(text: string): boolean {
  try {
    const { protocol } = new URL(text)
    return protocol === 'https:' || protocol === 'http:'
  } catch {
    return false
  }
}

const ACCEPTS: Record<ParamType, (value: unknown, choices?: readonly string[]) => boolean> = {
  string: (value) => typeof value === 'string' && fitsBytes(value),
  url: (value) => typeof value === 'string' && fitsBytes(value) && isWebAddress(value),
  number: (value) => typeof value === 'number' && storableNumber(value),
  boolean: (value) => typeof value === 'boolean',
  choice: (value, choices) => typeof value === 'string' && (choices ?? []).includes(value),
}

/** Whether `value` is one this setting may hold (§3's table). */
export function acceptsValue(declaration: Pick<ParamDeclaration, 'type' | 'choices'>, value: unknown): value is ParamValue {
  return ACCEPTS[declaration.type](value, declaration.choices)
}

function textProblem(declaration: ParamDeclaration, text: string): ParamProblem | null {
  if (!fitsBytes(text)) return 'too_long'
  return declaration.type === 'url' && !isWebAddress(text) ? 'url' : null
}

function parsedText(declaration: ParamDeclaration, raw: string): ParamEntry {
  if (declaration.type === 'number') {
    const text = raw.trim()
    if (text === '') return { ok: true, value: undefined }
    if (!NUMBER_TEXT.test(text)) return { ok: false, problem: 'number' }
    const n = Number(text)
    return storableNumber(n) ? { ok: true, value: n } : { ok: false, problem: 'number_range' }
  }
  if (declaration.type === 'boolean') return { ok: true, value: raw === 'true' }
  if (raw === '') return { ok: true, value: undefined }
  if (declaration.type === 'choice') return { ok: true, value: acceptsValue(declaration, raw) ? raw : undefined }
  const problem = textProblem(declaration, raw)
  return problem === null ? { ok: true, value: raw } : { ok: false, problem }
}

/**
 * What a control's text means for this setting. `value: undefined` is
 * "unset": an empty field, or any value equal to the default, so a stored
 * value is always one the learner set that differs from the default.
 */
export function valueFromText(declaration: ParamDeclaration, text: string): ParamEntry {
  const entry = parsedText(declaration, text)
  if (entry.ok && entry.value === declaration.default) return { ok: true, value: undefined }
  return entry
}

type Options = Record<string, unknown>

function fail(name: string, problem: string): never {
  throw new TypeError(`param ${name}: ${problem}`)
}

function checkChoices(name: string, type: ParamType, choices: unknown): readonly string[] | undefined {
  if (type !== 'choice') return choices === undefined ? undefined : fail(name, 'choices are only for a choice setting')
  const ok =
    Array.isArray(choices) &&
    choices.length > 0 &&
    choices.length <= MAX_ITEMS &&
    choices.every((choice) => ACCEPTS.string(choice))
  return ok ? [...(choices as string[])] : fail(name, `choices must be 1 to ${MAX_ITEMS} short strings`)
}

function checkDefault(name: string, type: ParamType, value: unknown, choices?: readonly string[]): ParamValue {
  // An optional `url` (§3): `''` is a default only, never a stored value.
  if (type === 'url' && value === '') return value
  return acceptsValue({ type, choices }, value) ? value : fail(name, `default is not a valid ${type} value`)
}

function checkName(name: unknown): string {
  if (typeof name === 'string' && PARAM_NAME.test(name)) return name
  throw new TypeError(`param name ${JSON.stringify(name)} must be lowercase letters, digits, - or _, starting with a letter`)
}

function optionsOf(name: string, options: unknown): Options {
  const plain = typeof options === 'object' && options !== null && !Array.isArray(options)
  return plain ? (options as Options) : fail(name, 'options must be an object')
}

function checkLabel(name: string, label: unknown): void {
  if (label !== undefined && (typeof label !== 'string' || label.length > MAX_STRING)) fail(name, 'label must be a short string')
}

/**
 * §2 step 2: the declaration `options` describe, or a `TypeError` naming the
 * option at fault.
 */
export function checkOptions(rawName: unknown, options: unknown): ParamDeclaration {
  const name = checkName(rawName)
  const o = optionsOf(name, options)
  const type = PARAM_TYPES.includes(o.type as ParamType) ? (o.type as ParamType) : fail(name, `type must be one of ${PARAM_TYPES.join(', ')}`)
  const choices = checkChoices(name, type, o.choices)
  const value = checkDefault(name, type, o.default, choices)
  checkLabel(name, o.label)
  return {
    name,
    type,
    default: value,
    ...(o.label === undefined ? {} : { label: o.label as string }),
    ...(choices === undefined ? {} : { choices }),
  }
}

function sameDeclaration(a: ParamDeclaration, b: ParamDeclaration): boolean {
  // Structural: the same choices in the same order (§2 step 3).
  const choices = JSON.stringify(a.choices ?? null) === JSON.stringify(b.choices ?? null)
  return a.type === b.type && a.default === b.default && a.label === b.label && choices
}

function tryCheck(entry: unknown): ParamDeclaration | null {
  try {
    const row = (typeof entry === 'object' && entry !== null ? entry : {}) as Options
    return checkOptions(row.name, row)
  } catch {
    return null
  }
}

/** A reply's declarations, re-checked: invalid and duplicate entries dropped (first wins), at most `MAX_PARAMS`. */
export function parseDeclarations(value: unknown): ParamDeclaration[] {
  const kept: ParamDeclaration[] = []
  for (const entry of Array.isArray(value) ? value : []) {
    if (kept.length === MAX_PARAMS) break
    const declaration = tryCheck(entry)
    if (declaration !== null && !kept.some((seen) => seen.name === declaration.name)) kept.push(declaration)
  }
  return kept
}

function isStorable(value: unknown): value is ParamValue {
  return typeof value === 'string' || typeof value === 'boolean' || (typeof value === 'number' && storableNumber(value))
}

/** A node's stored values from the wire: scalar entries under a valid name; `{}` for anything else, `null` included. */
export function narrowParamValues(value: unknown): ParamValues {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return {}
  return Object.fromEntries(Object.entries(value).filter(([key, entry]) => PARAM_NAME.test(key) && isStorable(entry)))
}

export interface ParamScope {
  param(name: string, options: unknown): ParamValue
  declared(): ParamDeclaration[]
}

/**
 * §2: one `param` over `values`. With `record`, each new name is kept (a 17th
 * throws, a second with different options throws); without it (the shared
 * main-thread `lingaraV1`), nothing is kept, so nothing fills.
 */
export function paramScope(values: ParamValues, record: boolean): ParamScope {
  const list: ParamDeclaration[] = []
  function param(name: string, options: unknown): ParamValue {
    const declaration = checkOptions(name, options)
    const seen = list.find((entry) => entry.name === declaration.name)
    if (seen !== undefined && !sameDeclaration(seen, declaration)) throw new TypeError(`param ${name} is declared twice`)
    if (record && seen === undefined) {
      if (list.length === MAX_PARAMS) throw new TypeError(`a script can declare at most ${MAX_PARAMS} settings`)
      list.push(declaration)
    }
    const value = Object.hasOwn(values, name) ? values[name] : undefined
    return acceptsValue(declaration, value) ? value : declaration.default
  }
  return { param, declared: () => list.map((entry) => ({ ...entry })) }
}
